import { useCallback, useEffect, useState } from "react";
import { useNotify } from "ra-core";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { BarChart3, Download, Trash2 } from "lucide-react";
import * as api from "./api";
import type { Funnel, CampaignPerf, AgentDay } from "./api";
import { TabBar, StatTile, SectionCard, Pill } from "../me-ui";

// ME → Reports (S5 / D60), re-skinned in me-ui (D64) — the final ME surface.
// READ-ONLY marketing/raw-lead/campaign/dialer reports over the KPI layer, sliced
// by date range + campaign. Metric math unchanged (report_* RPCs untouched).

const ALL = "__all__";
const iso = (d: Date) => d.toISOString().slice(0, 10);
const fmtTalk = (s: number) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
const money = (v: number | null) => (v == null ? "—" : `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);

// Single-series funnel: one hue, length ∝ magnitude relative to top-of-funnel,
// with per-stage step-conversion % (dataviz: no rainbow, length encodes value).
const FunnelBars = ({ f }: { f: Funnel }) => {
  const stages = [
    { label: "Raw leads", value: f.raw_leads },
    { label: "Contacted", value: f.contacted },
    { label: "Conversations", value: f.conversations },
    { label: "Promoted", value: f.promoted },
    { label: "Offers", value: f.offers },
    { label: "Contracts", value: f.contracts },
  ];
  const top = Math.max(1, stages[0].value);
  return (
    <div className="space-y-2.5">
      {stages.map((s, i) => {
        const prev = i > 0 ? stages[i - 1].value : null;
        const stepPct = prev && prev > 0 ? Math.round((s.value / prev) * 100) : null;
        return (
          <div key={s.label} className="flex items-center gap-3">
            <div className="w-28 text-sm text-muted-foreground text-right shrink-0">{s.label}</div>
            <div className="flex-1 h-7 rounded-md bg-muted/50 overflow-hidden">
              <div className="h-7 rounded-md bg-blue-600 dark:bg-blue-500 flex items-center justify-end pr-2 min-w-[2.5rem] transition-all"
                style={{ width: `${Math.max(3, (s.value / top) * 100)}%` }}>
                <span className="text-xs font-semibold text-white tabular-nums">{s.value.toLocaleString()}</span>
              </div>
            </div>
            <div className="w-12 text-xs text-muted-foreground tabular-nums text-right shrink-0" title="step conversion">{stepPct != null ? `${stepPct}%` : "—"}</div>
          </div>
        );
      })}
    </div>
  );
};

// ---- Aggregate tab ----
const AggregateTab = ({ from, to, campaignId }: { from: string; to: string; campaignId: string | null }) => {
  const [f, setF] = useState<Funnel | null>(null);
  useEffect(() => { api.loadFunnel(from, to, campaignId).then(setF); }, [from, to, campaignId]);
  if (!f) return <p className="text-sm text-muted-foreground">Loading…</p>;
  const convRate = f.contacted ? Math.round((f.conversations / f.contacted) * 1000) / 10 : 0;
  const contactRate = f.raw_leads ? Math.round((f.contacted / f.raw_leads) * 1000) / 10 : 0;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatTile label="Raw leads (in range)" value={f.raw_leads.toLocaleString()} />
        <StatTile label="Contacted %" value={`${contactRate}%`} />
        <StatTile label="Conversation %" value={`${convRate}%`} />
        <StatTile label="Promoted to S&A" value={f.promoted.toLocaleString()} />
      </div>
      <SectionCard title="Marketing funnel" sub="Stage counts + step conversion (of the previous stage)">
        <FunnelBars f={f} />
      </SectionCard>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <StatTile label="Marketing spend (range)" value={money(f.range_spend)} />
        <StatTile label="Cost / contact" value={money(f.cost_per_contact)} />
        <StatTile label="Cost / contract" value={money(f.cost_per_contract)} />
      </div>
      {f.range_spend == null && (
        <p className="text-xs text-muted-foreground">Enter a monthly total in the <b>Spend</b> tab to populate cost metrics for the selected range (D63).</p>
      )}
    </div>
  );
};

// ---- Spend tab (admin-entered monthly marketing spend; D63) ----
const SpendTab = () => {
  const notify = useNotify();
  const [rows, setRows] = useState<any[]>([]);
  const [canEdit, setCanEdit] = useState(false);
  const [month, setMonth] = useState(iso(new Date()).slice(0, 7));
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const reload = () => api.loadSpend().then(setRows);
  useEffect(() => { reload(); api.canManageSpend().then(setCanEdit); }, []);
  const add = async () => {
    if (!month || !amount) return;
    const { error } = await api.saveSpend(month, Number(amount), note);
    if (error) return notify(error.message, { type: "error" });
    setAmount(""); setNote(""); reload();
    notify("Spend saved", { type: "success" });
  };
  const del = async (id: string) => {
    if (!confirm("Delete this spend row?")) return;
    const { error } = await api.deleteSpend(id);
    if (error) return notify(error.message, { type: "error" });
    reload();
  };
  return (
    <div className="space-y-3">
      <SectionCard title="Monthly marketing spend" sub="Company-wide total per month — drives cost-per-contact/-contract on Aggregate (D63)"
        right={!canEdit ? <Pill tone="grey">Read-only</Pill> : null}>
        {canEdit ? (
          <div className="flex flex-wrap items-end gap-2">
            <div className="space-y-1"><Label className="text-xs">Month</Label><Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="h-9 w-40" /></div>
            <div className="space-y-1"><Label className="text-xs">Amount ($)</Label><Input type="number" min="0" value={amount} onChange={(e) => setAmount(e.target.value)} className="h-9 w-32" /></div>
            <div className="space-y-1 flex-1 min-w-40"><Label className="text-xs">Note</Label><Input value={note} onChange={(e) => setNote(e.target.value)} className="h-9" /></div>
            <Button onClick={add} disabled={!amount}>Save month</Button>
          </div>
        ) : <p className="text-sm text-muted-foreground">Admin-managed. Ask an Admin to enter monthly spend.</p>}
      </SectionCard>
      <div className="rounded-lg border overflow-x-auto bg-card">
        <Table>
          <TableHeader><TableRow><TableHead>Month</TableHead><TableHead>Amount</TableHead><TableHead>Note</TableHead>{canEdit && <TableHead></TableHead>}</TableRow></TableHeader>
          <TableBody>
            {rows.length === 0 ? <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-8">No spend entered.</TableCell></TableRow> :
              rows.map((r) => (
                <TableRow key={r.id}>
                  <TableCell className="font-medium">{String(r.period_month).slice(0, 7)}</TableCell>
                  <TableCell className="tabular-nums">${Number(r.amount).toLocaleString()}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{r.note ?? "—"}</TableCell>
                  {canEdit && <TableCell><Button size="icon" variant="ghost" onClick={() => del(r.id)}><Trash2 className="h-4 w-4 text-red-600" /></Button></TableCell>}
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </div>
      <p className="text-xs text-muted-foreground">Per-campaign cost isn't shown — a company-wide monthly spend doesn't decompose per campaign (D63).</p>
    </div>
  );
};

// ---- By Campaign tab ----
const CampaignTab = ({ from, to, onPick }: { from: string; to: string; onPick: (id: string | null) => void }) => {
  const [rows, setRows] = useState<CampaignPerf[]>([]);
  useEffect(() => { api.loadCampaignPerf(from, to).then(setRows); }, [from, to]);
  return (
    <div className="space-y-2">
      <div className="flex justify-end">
        <Button variant="outline" size="sm" onClick={() => api.downloadCsv(`campaigns_${from}_${to}.csv`, rows as any)}><Download className="mr-1 h-4 w-4" />Export CSV</Button>
      </div>
      <div className="rounded-lg border overflow-x-auto bg-card">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Campaign</TableHead><TableHead>Raw Leads</TableHead><TableHead>Dials</TableHead>
            <TableHead>Connects</TableHead><TableHead>Conv %</TableHead><TableHead>SMS</TableHead><TableHead>Mail</TableHead>
            <TableHead>Promoted</TableHead><TableHead>Offers</TableHead><TableHead>Deals</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {rows.length === 0 ? <TableRow><TableCell colSpan={10} className="text-center text-muted-foreground py-8">No data.</TableCell></TableRow> :
              rows.map((r, i) => (
                <TableRow key={i} className={r.campaign_id ? "cursor-pointer" : ""} onClick={() => r.campaign_id && onPick(r.campaign_id)}>
                  <TableCell className={r.campaign_id ? "font-semibold text-blue-700 dark:text-blue-400" : "font-medium text-muted-foreground"}>{r.campaign_name}</TableCell>
                  <TableCell className="tabular-nums">{r.raw_leads}</TableCell><TableCell className="tabular-nums">{r.dials}</TableCell><TableCell className="tabular-nums">{r.connects}</TableCell>
                  <TableCell className="tabular-nums">{r.conversation_rate != null ? `${Math.round(r.conversation_rate * 100)}%` : "—"}</TableCell>
                  <TableCell className="tabular-nums">{r.sms}</TableCell><TableCell className="tabular-nums">{r.mail}</TableCell>
                  <TableCell className="tabular-nums">{r.promoted}</TableCell><TableCell className="tabular-nums">{r.offers}</TableCell><TableCell className="tabular-nums">{r.deals}</TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

// ---- Call Reports tab ----
const CallsTab = ({ from, to, campaignId }: { from: string; to: string; campaignId: string | null }) => {
  const [rows, setRows] = useState<AgentDay[]>([]);
  useEffect(() => { api.loadCallsByAgent(from, to, campaignId).then(setRows); }, [from, to, campaignId]);
  return (
    <div className="space-y-2">
      <div className="flex justify-end">
        <Button variant="outline" size="sm" onClick={() => api.downloadCsv(`calls_${from}_${to}.csv`, rows as any)}><Download className="mr-1 h-4 w-4" />Export CSV</Button>
      </div>
      <div className="rounded-lg border overflow-x-auto bg-card">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Agent</TableHead><TableHead>Day</TableHead><TableHead>Dials</TableHead>
            <TableHead>Correct #</TableHead><TableHead>Conversations</TableHead><TableHead>Appts</TableHead>
            <TableHead>Talk</TableHead><TableHead>Conv %</TableHead><TableHead>Dials→Correct</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {rows.length === 0 ? <TableRow><TableCell colSpan={9} className="text-center text-muted-foreground py-8">No calls in range.</TableCell></TableRow> :
              rows.map((r, i) => (
                <TableRow key={i}>
                  <TableCell className="font-semibold">{r.agent_name ?? "—"}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{r.day}</TableCell>
                  <TableCell className="tabular-nums">{r.dials}</TableCell><TableCell className="tabular-nums">{r.correct_numbers}</TableCell><TableCell className="tabular-nums">{r.conversations}</TableCell>
                  <TableCell className="tabular-nums">{r.appointments}</TableCell><TableCell className="tabular-nums">{fmtTalk(r.talk_time_s)}</TableCell>
                  <TableCell className="tabular-nums">{r.conversation_rate != null ? `${Math.round(r.conversation_rate * 100)}%` : "—"}</TableCell>
                  <TableCell className="tabular-nums">{r.dials_to_correct != null ? `${Math.round(r.dials_to_correct * 100)}%` : "—"}</TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </div>
      <p className="text-xs text-muted-foreground">Correct # proxied by connected-disposition calls (no per-agent phone-status attribution).</p>
    </div>
  );
};

// ---- Shell ----
const ReportsPage = () => {
  const today = new Date();
  const monthAgo = new Date(); monthAgo.setDate(today.getDate() - 30);
  const [from, setFrom] = useState(iso(monthAgo));
  const [to, setTo] = useState(iso(today));
  const [campaign, setCampaign] = useState<string>(ALL);
  const [campaignOpts, setCampaignOpts] = useState<{ id: string; name: string }[]>([]);
  const [tab, setTab] = useState("aggregate");
  useEffect(() => { api.loadCampaignOptions().then(setCampaignOpts); }, []);
  const campaignId = campaign === ALL ? null : campaign;
  const pickCampaign = useCallback((id: string | null) => { setCampaign(id ?? ALL); setTab("aggregate"); }, []);

  return (
    <div className="p-2 max-w-6xl">
      <h1 className="text-[22px] font-extrabold tracking-tight flex items-center gap-2"><BarChart3 className="h-5 w-5 text-blue-600" /> Reports</h1>
      <p className="text-sm text-muted-foreground mb-3">Marketing / raw-lead / campaign / dialer reports. Read-only.</p>

      <SectionCard title="Filters" className="mb-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1"><Label className="text-xs">From</Label><Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-9 w-40" /></div>
          <div className="space-y-1"><Label className="text-xs">To</Label><Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-9 w-40" /></div>
          <div className="space-y-1"><Label className="text-xs">Campaign</Label>
            <Select value={campaign} onValueChange={setCampaign}>
              <SelectTrigger className="h-9 w-56"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All campaigns</SelectItem>
                {campaignOpts.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
      </SectionCard>

      <TabBar accent="blue" className="mb-4" value={tab} onChange={setTab} tabs={[
        { value: "aggregate", label: "Aggregate" },
        { value: "campaign", label: "By Campaign" },
        { value: "calls", label: "Call Reports" },
        { value: "spend", label: "Spend" },
      ]} />
      {tab === "aggregate" && <AggregateTab from={from} to={to} campaignId={campaignId} />}
      {tab === "campaign" && <CampaignTab from={from} to={to} onPick={pickCampaign} />}
      {tab === "calls" && <CallsTab from={from} to={to} campaignId={campaignId} />}
      {tab === "spend" && <SpendTab />}
    </div>
  );
};

ReportsPage.path = "/reports-me";
export default ReportsPage;
